/** PROTOTYPE spread setup header -- THROWAWAY. Length -> joints -> timeline. */
import React from 'react';
import { CRC } from './moduleInputsData';
import { Label, Segmented } from './protoAtoms';
import {
  Spread, PIPE_LENGTH_OPTIONS, computedJoints, joints, jointsOverridden,
  workingDays, durationMonthsFor,
} from './spreadSplitData';

interface Props { spread: Spread; patch: (patch: Partial<Spread>) => void; }

const box: React.CSSProperties = {
  width: 96, padding: '8px 10px', fontFamily: 'Funnel Sans, sans-serif', fontSize: 14, fontWeight: 600,
  color: CRC.blueprintBlue, border: `1px solid ${CRC.border}`, borderRadius: 8,
};
const field = (label: string, node: React.ReactNode) => (
  <div><Label>{label}</Label><div style={{ marginTop: 5 }}>{node}</div></div>
);
const readout = (label: string, value: string) => (
  <div><Label>{label}</Label><div style={{ fontFamily: 'Funnel Sans', fontSize: 18, fontWeight: 700, color: CRC.blueprintBlue }}>{value}</div></div>
);

export const SpreadSetup: React.FC<Props> = ({ spread, patch }) => {
  const isCustomPipe = !PIPE_LENGTH_OPTIONS.includes(spread.pipeLengthFt);
  return (
    <div style={{ background: '#fff', border: `1px solid ${CRC.border}`, borderTop: `3px solid ${CRC.heroRed}`, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {field('Spread name', (
          <input title="Spread name" value={spread.name} onChange={(e) => patch({ name: e.target.value })}
            style={{ ...box, width: 130, color: CRC.blueprintBlue }} />
        ))}

        {field('Length', (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input title="Spread length" type="number" value={spread.lengthValue}
              onChange={(e) => patch({ lengthValue: Math.max(0, Number(e.target.value)) })} style={box} />
            <Segmented value={spread.lengthUnit} onChange={(v) => patch({ lengthUnit: v as Spread['lengthUnit'] })}
              options={[{ value: 'mi', label: 'mi' }, { value: 'km', label: 'km' }]} />
          </div>
        ))}

        {field('Pipe joint length', (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Segmented value={isCustomPipe ? 'custom' : String(spread.pipeLengthFt)}
              onChange={(v) => patch({ pipeLengthFt: v === 'custom' ? 60 : Number(v) })}
              options={[...PIPE_LENGTH_OPTIONS.map((p) => ({ value: String(p), label: `${p} ft` })), { value: 'custom', label: 'Custom' }]} />
            {isCustomPipe && (
              <input title="Custom pipe length" type="number" value={spread.pipeLengthFt}
                onChange={(e) => patch({ pipeLengthFt: Math.max(1, Number(e.target.value)) })} style={{ ...box, width: 74 }} />
            )}
          </div>
        ))}

        {field('Joints', (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input title="Joints" type="number" value={joints(spread)}
              onChange={(e) => patch({ jointsOverride: Math.max(0, Number(e.target.value)) })}
              style={{ ...box, border: `1.5px solid ${jointsOverridden(spread) ? CRC.heroRed : CRC.border}`, background: jointsOverridden(spread) ? CRC.heroRed10 : '#fff' }} />
            {jointsOverridden(spread)
              ? <button onClick={() => patch({ jointsOverride: null })} style={{ fontSize: 11, color: CRC.readableRed, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>reset</button>
              : <span style={{ fontSize: 11, color: CRC.heritageGrey }}>= {computedJoints(spread).toLocaleString()} computed</span>}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16, borderTop: `1px solid ${CRC.rowDivider}`, paddingTop: 14 }}>
        {field('Timeline from', (
          <Segmented value={spread.throughputMode} onChange={(v) => patch({ throughputMode: v as Spread['throughputMode'] })}
            options={[{ value: 'perDay', label: 'Joints / day' }, { value: 'duration', label: 'Duration' }]} />
        ))}
        {spread.throughputMode === 'perDay'
          ? field('Joints per day', (
            <input title="Joints per day" type="number" value={spread.jointsPerDay}
              onChange={(e) => patch({ jointsPerDay: Math.max(0, Number(e.target.value)) })} style={box} />
          ))
          : field('Duration (months)', (
            <input title="Duration months" type="number" value={spread.durationMonths}
              onChange={(e) => patch({ durationMonths: Math.max(0, Number(e.target.value)) })} style={box} />
          ))}
        {spread.throughputMode === 'perDay' && readout('Working days', workingDays(spread).toLocaleString())}
        {readout('Duration', `${durationMonthsFor(spread)} mo`)}
      </div>
    </div>
  );
};
